/**
 * Shipped sub-tab lists for the sections whose bars are user-arrangeable.
 *
 * Kept OUT of the route layout so both sides can import it without a client
 * component reaching into a server layout module: `app/timing/layout.tsx`
 * renders it, and Settings → Pages edits its order. The saved order lives in
 * `lib/nav/subTabOrder.ts` and is merged over these defaults on read, so
 * editing a list here re-orders fresh profiles without disturbing anyone who
 * has already arranged their own.
 */
export type SectionTab = { href: string; label: string };

/** Section id for the persisted Market Timing sub-tab order. */
export const TIMING_SECTION = "timing";

/** Section id for the persisted Wealth sub-tab order. */
export const WEALTH_SECTION = "wealth";

/**
 * Market Timing, in shipped order: the market-read pages first (Timing Brief →
 * Buckets Brief → the three Breadth boards), then the chart-study pages (ETFs,
 * Guppy, Ratio, Macro, COT, Cycles). Live Breadth and Daily Brief are top-level nav pages
 * (`/live-breadth`, `/daily-brief`, `/outlook`), not Market Timing sub-tabs — same
 * split as Scanner. Daily Brief's route moved out of `/timing/*` as well: staying under
 * it meant `app/timing/layout.tsx` kept rendering this bar above a page that is
 * no longer one of its tabs. `/timing/daily-brief` redirects. A saved sub-tab
 * order naming it is dropped on read by `orderTabs`, which emits only hrefs
 * that still ship — no migration needed.
 */
export const TIMING_TABS: ReadonlyArray<SectionTab> = [
  { href: "/timing/timing-brief", label: "Timing Brief" },
  { href: "/timing/buckets", label: "Buckets Brief" },
  { href: "/timing/breadth-1", label: "Market Breadth 1" },
  { href: "/timing/breadth-2", label: "Market Breadth 2" },
  { href: "/timing/breadth-3", label: "Market Breadth 3" },
  { href: "/timing/index-etfs", label: "US Index ETFs" },
  { href: "/timing/commodities-etfs", label: "Commodities ETFs" },
  { href: "/timing/guppy", label: "Guppy Charts" },
  { href: "/timing/ratio", label: "Ratio Charts" },
  { href: "/timing/macro", label: "Macro" },
  // The CFTC's Commitments of Traders (2026-10-16), after Macro. A saved
  // order, which `orderTabs` merges over this list, shows it at the end.
  { href: "/timing/cot", label: "COT" },
  // Market cycles: FSC's cycle scanner, Hurst, Ehlers and the wavelet map
  // (2026-10-18), after COT.
  { href: "/timing/cycles", label: "Cycles" },
  // Pradeep Bonde's breadth tracker (2026-10-18), after Cycles.
  { href: "/timing/market-monitor", label: "Market Monitor" },
  // What the big managers bought and sold, from their 13F filings: the
  // fifteen named funds and every filer (2026-10-23), after Market Monitor.
  { href: "/timing/13f", label: "13F" },
  // The economic releases and earnings by week or month, after SweeterTrade's
  // Calendar (2026-11-03), after 13F.
  { href: "/timing/calendar", label: "Calendar" },
];

/** Section id for the persisted Options sub-tab order. */
export const OPTIONS_SECTION = "options";

/**
 * Options, in shipped order. One route (`/options`) with the tab in the query,
 * not sub-routes: every tab shares ONE OptionsProvider, so the working trade,
 * symbol and expiry survive a tab switch. A sub-route per tab would remount the
 * section layout (`withLayout` builds a new component per route) and drop the
 * position you were building. Trade is the bare `/options`, so a shared trade
 * link (`/options?symbol=…&b=…`) and every old bookmark land on it.
 */
export const OPTIONS_TABS: ReadonlyArray<SectionTab> = [
  { href: "/options", label: "Trade" },
  { href: "/options?tab=analyze", label: "Analyze" },
  { href: "/options?tab=flow", label: "Flow" },
  { href: "/options?tab=scan", label: "Scan" },
];

/**
 * Wealth, in shipped order. These are routes under `/wealth`, rendered by
 * `app/wealth/layout.tsx` as the same numbered, drag-arrangeable bar Market
 * Timing uses. Each page is a workspace of movable boxes.
 */
export const WEALTH_TABS: ReadonlyArray<SectionTab> = [
  { href: "/wealth", label: "Overview" },
  { href: "/wealth/holdings", label: "Investments" },
  { href: "/wealth/property", label: "Property & debts" },
  // Transactions and Budgets merged into Spending (their old routes redirect).
  // A saved tab order naming them is dropped on read by `orderTabs`.
  { href: "/wealth/spending", label: "Spending" },
  { href: "/wealth/accounts", label: "Accounts" },
  // Trade Management folded in 2026-10-01: its accounts ARE Wealth's books and
  // its open trades ARE their positions. `/trades` redirects here.
  { href: "/wealth/trades", label: "Trade Management" },
  // The household projected month by month (2026-10-02). New tabs go last so a
  // saved order, which `orderTabs` merges over this list, keeps its own order
  // and shows the new tab at the end.
  { href: "/wealth/forecast", label: "Forecast" },
];
